import {DeckSession} from '../core/deck-session';
import {DeckWorkspace} from '../core/deck-workspace';
import {PresenterBridge} from '../core/presenter-bridge';
import {routeUrl, syncRoute} from '../core/routing';
import {RemoteControl} from './remote-control';
import {SlideStage} from '../render/slide-stage';
import type {WorkspaceAsset} from '../core/model';
import {button, element, iconButton} from './dom';
import {ignoresPresentationHotkeys, isInteractiveTarget, isRepeatedPresentationHotkey} from './keyboard-navigation';
import {bindWheelNavigation} from './wheel-navigation';
import {toggleFullscreen} from './fullscreen';
import {AudienceVisibility} from './audience-visibility';
import {actionMenu} from './action-menu';
import {withIcon} from './workspace-icons';

export class PresenterView {
  readonly element: HTMLElement;
  #workspace: DeckWorkspace;
  #session: DeckSession;
  #bridge: PresenterBridge;
  #remote: RemoteControl;
  #room: string | undefined;
  #stage: SlideStage;
  #blackout: HTMLElement;
  #visibility!: AudienceVisibility;
  #controls: HTMLElement;
  #cursorTimer = 0;

  constructor(
    workspace: DeckWorkspace,
    page: number,
    step: number,
    assets: WorkspaceAsset[] = [],
    room: string | null = null,
  ) {
    this.#workspace = workspace;
    this.#session = new DeckSession(workspace.source, {page, step});
    this.#bridge = new PresenterBridge(workspace.deckId);
    this.#room = room ?? undefined;
    this.element = element('main', 'presenter');
    const canvas = element('div', 'presenter-canvas');
    const assetUrls = new Map(assets.map((asset) => [asset.name, asset.url]));
    this.#stage = new SlideStage(canvas, {
      deckId: workspace.deckId,
      focusVideos: true,
      resolveAsset: (source) =>
        source.startsWith('assets/')
          ? assetUrls.get(source.slice(7)) ?? workspace.assetUrl(source)
          : source,
    });
    this.#blackout = element('div', 'blackout');
    this.#controls = this.createControls();
    // Built last: the remote reads the blackout element and the stage, so it
    // cannot exist before them.
    this.#remote = new RemoteControl({
      deckId: workspace.deckId,
      room,
      role: 'host',
      currentState: () => ({...this.#session.location, blackout: this.isBlackedOut()}),
      applyState: (state) => {
        this.#session.go(state.page, state.step);
        if (state.blackout !== this.isBlackedOut()) {
          this.setBlackout(state.blackout);
          this.#bridge.sendBlackout(state.blackout);
        }
      },
      onRoomChange: (code) => {
        this.#room = code ?? undefined;
        syncRoute(
          'present',
          this.#workspace.deckId,
          this.#session.location.page,
          this.#session.location.step,
          code ?? undefined,
        );
      },
      peerUrl: (code) =>
        routeUrl(
          'speaker',
          this.#workspace.deckId,
          this.#session.location.page,
          this.#session.location.step,
          code,
        ),
    });
    this.#controls.append(this.#remote.element);
    this.element.append(canvas, this.#blackout, this.#controls);
    this.bind();
    this.render();
    if (matchMedia('(pointer: coarse)').matches) this.showControls();
  }

  private createControls(): HTMLElement {
    const controls = element('div', 'presenter-controls');
    const navigation = element('div', 'presenter-navigation');
    const back = iconButton('Previous', 'previous');
    back.addEventListener('click', () => this.#session.backward());
    const counter = element('span', 'presenter-counter');
    counter.dataset.role = 'presenter-counter';
    const next = iconButton('Next', 'next');
    next.addEventListener('click', () => this.#session.forward());
    navigation.append(back, counter, next);

    const actions = element('div', 'presenter-actions');
    this.#visibility = new AudienceVisibility(() => this.toggleBlackout());
    const speaker = withIcon(button('Speaker view', 'button button-quiet'), 'speaker');
    speaker.addEventListener('click', () => {
      window.open(
        routeUrl(
          'speaker',
          this.#workspace.deckId,
          this.#session.location.page,
          this.#session.location.step,
          this.#room,
        ),
        'slides2-speaker',
      );
    });
    const fullscreen = iconButton('Fullscreen', 'fullscreen');
    fullscreen.addEventListener('click', () => void toggleFullscreen());
    actions.append(speaker, fullscreen, actionMenu('More', [this.#visibility.action], 'more'));
    controls.append(this.#visibility.status, navigation, actions);
    return controls;
  }

  private bind(): void {
    bindWheelNavigation(this.element, (step) => {
      if (step === 'forward') this.#session.forward();
      else this.#session.backward();
    });
    document.addEventListener('keydown', (event) => {
      if (ignoresPresentationHotkeys(event)) return;
      if (isRepeatedPresentationHotkey(event)) {
        event.preventDefault();
        return;
      }
      if (event.key === ' ' || event.key === 'ArrowRight' || event.key === 'PageDown') {
        event.preventDefault();
        this.#session.forward();
      } else if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault();
        this.#session.backward();
      } else if (event.key.toLowerCase() === 'b') {
        this.toggleBlackout();
      } else if (event.key.toLowerCase() === 'f') {
        void toggleFullscreen();
      } else if (event.key.toLowerCase() === 's') {
        window.open(
          routeUrl('speaker', this.#workspace.deckId, this.#session.location.page, this.#session.location.step, this.#room),
          'slides2-speaker',
        );
      } else if (event.key === 'Home') {
        this.#session.go(1);
      } else if (event.key === 'End') {
        this.#session.go(this.#session.deck.slides.length);
      }
    });
    this.element.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'touch') this.showControls();
    });
    this.element.addEventListener('click', (event) => {
      if ((event.target as Element).closest('.presenter-controls')) {
        this.showControls();
        return;
      }
      if (isInteractiveTarget(event.target) || event.defaultPrevented) return;
      const touch =
        (event as PointerEvent).pointerType === 'touch' ||
        matchMedia('(pointer: coarse)').matches;
      if (touch && !this.#controls.classList.contains('is-visible')) {
        this.showControls();
        return;
      }
      const x = (event as PointerEvent).clientX;
      if (x < innerWidth * 0.25) this.#session.backward();
      else this.#session.forward();
    });
    this.#session.onChange(() => {
      this.render();
      this.#bridge.sendLocation(this.#session.location);
      this.#remote.publishLocal();
    });
    this.#bridge.listen((message) => {
      if (message.type === 'blackout') this.setBlackout(message.value);
      if (message.type === 'location') this.#session.go(message.location.page, message.location.step);
      if (message.type === 'command') {
        if (message.command === 'forward') this.#session.forward();
        if (message.command === 'backward') this.#session.backward();
        if (message.command === 'blackout') this.toggleBlackout();
      }
    });
    this.#bridge.requestBlackout();
  }

  private render(): void {
    const location = this.#session.location;
    const slide = this.#session.deck.slides[location.page - 1];
    if (!slide) return;
    this.#stage.render(slide, location.step, {
      chains: this.#session.deck.chains,
      language: this.#session.deck.language,
      theme: this.#session.deck.theme,
      style: this.#session.deck.style,
    });
    syncRoute('present', this.#workspace.deckId, location.page, location.step, this.#room);
    const counter = this.element.querySelector('[data-role="presenter-counter"]');
    if (counter) {
      counter.textContent = `${location.page} / ${this.#session.deck.slides.length}${
        slide.revealCount ? ` · ${location.step}/${slide.revealCount}` : ''
      }`;
    }
  }

  private isBlackedOut(): boolean {
    return this.#blackout.classList.contains('is-visible');
  }

  /** Absolute, never a toggle: a command that arrives twice must not undo itself. */
  private setBlackout(blacked: boolean): void {
    this.#stage.setMediaSuspended(blacked);
    this.#blackout.classList.toggle('is-visible', blacked);
    this.element.classList.toggle('is-audience-hidden', blacked);
    this.#visibility.sync(blacked);
    if (blacked) this.showControls();
  }

  private toggleBlackout(): void {
    this.setBlackout(!this.isBlackedOut());
    this.#bridge.sendBlackout(this.isBlackedOut());
    this.#remote.publishLocal();
  }

  private showControls(): void {
    this.#controls.classList.add('is-visible');
    clearTimeout(this.#cursorTimer);
    const delay = matchMedia('(pointer: coarse)').matches ? 4_000 : 1_800;
    this.#cursorTimer = window.setTimeout(() => this.#controls.classList.remove('is-visible'), delay);
  }
}
